import { useState, useCallback, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { UnlinkedBatch } from '../../../../types/batch';
import { WorkflowFlowType } from '../../../../types/workflow';
import { Farmer } from '../../../../types/farmers';
import { Farm } from '../../../../types/farms';
import { batchService } from '../../../../services/batchService';
import { farmerService } from '../../../../services/farmerService';
import { farmService } from '../../../../services/farmService';

/**
 * Page size for the client-side prefetch of the FULL farmer/farm option lists.
 *
 * The workflow dialog fetches every farmer/farm up front and filters them in the dropdowns (there is
 * no slim options endpoint yet — that migration is tracked separately). The previous cap of 500
 * silently TRUNCATED the lists once the population exceeded it (~643 farms today), so a real farm
 * could be missing from the picker with no error. This ceiling sits well above the current counts
 * (~643 farms / ~440 farmers) with ample headroom; neither the Farm nor Farmer list endpoint clamps
 * PageSize server-side, so the whole set is returned. Superseded once the slim endpoint lands.
 */
const FORM_OPTIONS_PAGE_SIZE = 5000;

/**
 * Form data state containing all fetched entities.
 */
interface WorkflowFormData {
  batches: UnlinkedBatch[];
  farmers: Farmer[];
  farms: Farm[];
}

/**
 * Loading state for each data type.
 */
interface LoadingState {
  batches: boolean;
  farmers: boolean;
  farms: boolean;
}

/**
 * Return type for the useWorkflowFormData hook.
 */
interface UseWorkflowFormDataReturn {
  /** All fetched form data */
  data: WorkflowFormData;
  /** Loading states for each data type */
  loading: LoadingState;
  /** Refetch farms filtered by farmer ID */
  fetchFarmsByFarmer: (farmerId: number) => Promise<void>;
  /** Reset all data to initial state */
  resetData: () => void;
  /** Add a newly created farmer to the data array (with deduplication) */
  addFarmer: (farmer: Farmer) => void;
  /** Add a newly created farm to the data array (with deduplication) */
  addFarm: (farm: Farm) => void;
}

const INITIAL_DATA: WorkflowFormData = {
  batches: [],
  farmers: [],
  farms: [],
};

const INITIAL_LOADING: LoadingState = {
  batches: false,
  farmers: false,
  farms: false,
};

/**
 * Custom hook for managing workflow form data fetching.
 *
 * Consolidates all data fetching logic from CreateBatchDialog:
 * - Batches (for ExistingBatch flow — Path B deferred)
 * - Farmers, Farms, CropSubTypes (for NewRequest flow)
 *
 * Features:
 * - Flow-type-based conditional fetching
 * - Proper loading state management
 * - Error handling with snackbar notifications
 *
 * @param open - Whether the dialog is open
 * @param flowType - Current workflow flow type ('NewRequest' or 'LabTriggered')
 */
export const useWorkflowFormData = (
  open: boolean,
  flowType: WorkflowFlowType
): UseWorkflowFormDataReturn => {
  const { enqueueSnackbar } = useSnackbar();

  // Consolidated data state
  const [data, setData] = useState<WorkflowFormData>(INITIAL_DATA);

  // Consolidated loading state
  const [loading, setLoading] = useState<LoadingState>(INITIAL_LOADING);

  /**
   * Helper to update a single loading state.
   */
  const setLoadingFor = useCallback(
    (key: keyof LoadingState, value: boolean) => {
      setLoading((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  /**
   * Helper to update a single data state.
   */
  const setDataFor = useCallback(
    <K extends keyof WorkflowFormData>(key: K, value: WorkflowFormData[K]) => {
      setData((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  /**
   * Fetch active batches (for with-batch mode).
   */
  const fetchBatches = useCallback(async () => {
    setLoadingFor('batches', true);
    try {
      const response = await batchService.getUnlinkedBatches();
      if (response.data) {
        setDataFor('batches', response.data);
      }
    } catch {
      enqueueSnackbar('Failed to load batches', { variant: 'error' });
    } finally {
      setLoadingFor('batches', false);
    }
  }, [enqueueSnackbar, setLoadingFor, setDataFor]);

  /**
   * Fetch available farmers.
   */
  const fetchFarmers = useCallback(async () => {
    setLoadingFor('farmers', true);
    try {
      const response = await farmerService.getFarmers({
        pageSize: FORM_OPTIONS_PAGE_SIZE,
      });
      setDataFor('farmers', response.items);
    } catch {
      enqueueSnackbar('Failed to load farmers', { variant: 'error' });
    } finally {
      setLoadingFor('farmers', false);
    }
  }, [enqueueSnackbar, setLoadingFor, setDataFor]);

  /**
   * Fetch farms, optionally filtered by farmer ID.
   */
  const fetchFarms = useCallback(
    async (farmerId?: number) => {
      setLoadingFor('farms', true);
      try {
        const response = await farmService.getFarms({
          pageSize: FORM_OPTIONS_PAGE_SIZE,
          idFarmer: farmerId,
        });
        setDataFor('farms', response.items);
      } catch {
        enqueueSnackbar('Failed to load farms', { variant: 'error' });
      } finally {
        setLoadingFor('farms', false);
      }
    },
    [enqueueSnackbar, setLoadingFor, setDataFor]
  );

  /**
   * Public method to fetch farms by farmer ID.
   * Used when farmer selection changes in standalone mode.
   */
  const fetchFarmsByFarmer = useCallback(
    async (farmerId: number) => {
      await fetchFarms(farmerId);
    },
    [fetchFarms]
  );

  /**
   * Add a newly created farmer to the data array (with deduplication).
   */
  const addFarmer = useCallback((farmer: Farmer) => {
    setData(prev => ({
      ...prev,
      farmers: [farmer, ...prev.farmers.filter(f => f.idFarmer !== farmer.idFarmer)],
    }));
  }, []);

  /**
   * Add a newly created farm to the data array (with deduplication).
   */
  const addFarm = useCallback((farm: Farm) => {
    setData(prev => ({
      ...prev,
      farms: [farm, ...prev.farms.filter(f => f.idFarm !== farm.idFarm)],
    }));
  }, []);

  /**
   * Reset all data to initial state.
   * Called when dialog closes.
   */
  const resetData = useCallback(() => {
    setData(INITIAL_DATA);
    setLoading(INITIAL_LOADING);
  }, []);

  // Fetch batches only in ExistingBatch flow (Path B — deferred)
  useEffect(() => {
    if (open && flowType === 'ExistingBatch') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch on dialog open; only the loading flag is set synchronously, data arrives asynchronously
      fetchBatches();
    }
  }, [open, flowType, fetchBatches]);

  // Fetch farmers, farms, cropSubTypes for NewRequest and LabTriggered flows
  useEffect(() => {
    if (open && (flowType === 'NewRequest' || flowType === 'LabTriggered')) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetches on dialog open; only loading flags are set synchronously, data arrives asynchronously
      fetchFarmers();
      fetchFarms();
    }
  }, [open, flowType, fetchFarmers, fetchFarms]);

  return {
    data,
    loading,
    fetchFarmsByFarmer,
    resetData,
    addFarmer,
    addFarm,
  };
};

export default useWorkflowFormData;
